/**
 * Shared audit trail export utilities — CSV and PDF (print-to-PDF via browser)
 */

export interface AuditExportRow {
  [key: string]: string | number | null | undefined;
}

/** Convert an array of objects to a CSV string */
function toCSV(rows: AuditExportRow[], columns: { key: string; label: string }[]): string {
  const header = columns.map((c) => `"${c.label}"`).join(',');
  const body = rows.map((row) =>
    columns
      .map((c) => {
        const val = row[c.key] ?? '';
        return `"${String(val).replace(/"/g, '""')}"`;
      })
      .join(',')
  );
  return [header, ...body].join('\r\n');
}

/** Trigger a browser download of a text file */
function downloadText(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Download rows as a CSV file */
export function downloadCSV(
  rows: AuditExportRow[],
  columns: { key: string; label: string }[],
  filename: string
) {
  const csv = toCSV(rows, columns);
  downloadText(csv, filename, 'text/csv;charset=utf-8;');
}

/** Generate a printable HTML page and open the browser print dialog (Save as PDF) */
export function downloadPDF(
  rows: AuditExportRow[],
  columns: { key: string; label: string }[],
  title: string,
  subtitle?: string
) {
  const now = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  const headerRow = columns.map((c) => `<th>${c.label}</th>`).join('');
  const bodyRows = rows
    .map((row) => {
      const cells = columns
        .map((c) => `<td>${String(row[c.key] ?? '—').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`)
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 11px; color: #111; padding: 24px; }
    .header { margin-bottom: 16px; border-bottom: 2px solid #1a1a2e; padding-bottom: 12px; }
    .header h1 { font-size: 18px; font-weight: 700; color: #1a1a2e; }
    .header p { font-size: 11px; color: #555; margin-top: 4px; }
    .meta { display: flex; gap: 24px; margin-bottom: 16px; font-size: 10px; color: #666; }
    .meta span strong { color: #111; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    thead tr { background: #1a1a2e; color: #fff; }
    thead th { padding: 7px 8px; text-align: left; font-weight: 600; white-space: nowrap; }
    tbody tr:nth-child(even) { background: #f5f5f5; }
    tbody td { padding: 6px 8px; border-bottom: 1px solid #e0e0e0; vertical-align: top; word-break: break-word; max-width: 200px; }
    .footer { margin-top: 16px; font-size: 9px; color: #888; text-align: center; border-top: 1px solid #ddd; padding-top: 8px; }
    @media print {
      body { padding: 12px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${title}</h1>
    ${subtitle ? `<p>${subtitle}</p>` : ''}
  </div>
  <div class="meta">
    <span><strong>Generated:</strong> ${now}</span>
    <span><strong>Total Records:</strong> ${rows.length}</span>
    <span><strong>Classification:</strong> Confidential — Regulatory Use Only</span>
  </div>
  <table>
    <thead><tr>${headerRow}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
  <div class="footer">
    CrowdVault™ Platform — Immutable Audit Trail Export — ${now} — Page <span class="pagenum"></span>
  </div>
  <script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
